<script setup lang="ts">
    import { computed, ref, watch, provide } from 'vue';
    import { RouterView, useRoute } from 'vue-router';
    import ContactList from '@views/Lists/ContactList.vue';
    import * as smsStatsApi from '../TextMessageStatistics.access';
    import type { ContactsResponse } from '../TextMessageStatistics.access';
    import { getSmsStatisticsContactListHeadings, getSmsStatisticsContactListDescriptions } from '../sms-statistics-types';
    import { loadContact } from '@views/Contact/Contact.access';

    const props = defineProps<{
        smsMessageId: string;
        contactType: string;
        dateRange: { start: string | null; end: string | null };
        contactFilter?: string;
    }>();

    const route = useRoute();
    const showingContact = computed(() => route.name === 'TextMessageStatisticsContact');
    const contactListRef = ref<InstanceType<typeof ContactList> | null>(null);
    const lastViewedContactId = ref<string | null>(null);
    const contactWasUpdated = ref(false);

    provide('markContactUpdated', () => {
        contactWasUpdated.value = true;
    });

    watch(
        () => route.params.contactId as string | undefined,
        (id) => {
            if (id) {
                lastViewedContactId.value = id;
                contactWasUpdated.value = false;
            }
        }
    );

    watch(showingContact, async (showing, wasShowing) => {
        if (wasShowing && !showing && lastViewedContactId.value && contactWasUpdated.value) {
            const updated = await loadContact(lastViewedContactId.value);
            contactListRef.value?.updateContact(updated);
        }
    });

    function contactRoute(contactId: string) {
        return {
            name: 'TextMessageStatisticsContact',
            params: { smsMessageId: props.smsMessageId, contactType: props.contactType, contactId },
            query: { ...route.query },
        };
    }

    const headings = getSmsStatisticsContactListHeadings();
    const descriptions = getSmsStatisticsContactListDescriptions();

    const title = computed(() => headings[props.contactType] ?? props.contactType);
    const description = computed(() => descriptions[props.contactType]);

    function buildQs(): string {
        return smsStatsApi.buildQueryString(props.dateRange, props.contactFilter || null);
    }

    async function fetchContacts(skip: number, pageSize: number, extra: string): Promise<ContactsResponse> {
        return smsStatsApi.getContacts(props.smsMessageId, props.contactType, buildQs(), skip, pageSize, extra);
    }

    async function exportExcel(extra: string): Promise<{ Url: string }> {
        return smsStatsApi.exportContactsExcel(props.smsMessageId, props.contactType, buildQs(), extra);
    }

    async function exportCsv(extra: string): Promise<{ Url: string }> {
        return smsStatsApi.exportContactsCsv(props.smsMessageId, props.contactType, buildQs(), extra);
    }
</script>

<template>
    <RouterView v-if="showingContact" />

    <div v-show="!showingContact">
        <ContactList
            ref="contactListRef"
            :key="`${contactType}-${dateRange.start}-${dateRange.end}-${contactFilter}`"
            :contactRoute="contactRoute"
            :title="title"
            :description="description"
            :hideAddedDateTime="true"
            :fetchContacts="fetchContacts"
            :exportExcel="exportExcel"
            :exportCsv="exportCsv" />
    </div>
</template>
